/**
 * LoginPage — timeout + next= + agentes → /workspace
 */
import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase }        from '@/lib/supabase'
import { useAuthStore }    from '@/stores/auth.store'
import { LOGO_PROCREDITO } from '@/assets/logo'
import { cn }               from '@/lib/utils'
import {
  Eye, EyeOff, Loader2, AlertCircle, Clock, Ban,
  ShieldCheck, BarChart2, Settings2, Users2, Phone, Target, Headphones,
} from 'lucide-react'

// Cuando esta página corre dentro del .exe instalado (ver electron/preload.js),
// se muestra como "Call Center" en vez del branding genérico del CRM. Es
// puramente visual — el formulario, la autenticación y el destino post-login
// son EXACTAMENTE los mismos, no se toca nada de esa lógica.
const isDesktopApp = typeof window !== 'undefined' && Boolean((window as unknown as { procreditoDesktop?: { isDesktopApp?: boolean } }).procreditoDesktop?.isDesktopApp)

function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = window.setTimeout(() => reject(new Error(`${label} (timeout ${ms / 1000}s)`)), ms)
    p.then(
      (v) => { window.clearTimeout(t); resolve(v) },
      (e) => { window.clearTimeout(t); reject(e) },
    )
  })
}

async function resolveCredential(identifier: string): Promise<string> {
  if (identifier.includes('@')) return identifier

  const { data, error } = await withTimeout(
    supabase.rpc('get_email_by_username', { p_username: identifier.toLowerCase().trim() }),
    8000,
    'No se pudo resolver el usuario',
  )

  if (error || !data) {
    throw new Error('Usuario no encontrado. Verifica tu nombre de usuario.')
  }
  return data as string
}

function safeNextPath(raw: string | null): string | null {
  if (!raw) return null
  try {
    const decoded = decodeURIComponent(raw)
    if (decoded.startsWith('/') && !decoded.startsWith('//')) return decoded
  } catch { /* ignore */ }
  return null
}

/** Destino post-login: ?next= tiene prioridad; si no, agente → workspace */
async function resolvePostLoginPath(userId: string, nextParam: string | null): Promise<string> {
  if (nextParam) return nextParam
  try {
    const { data } = await withTimeout(
      supabase.from('profiles').select('role').eq('id', userId).maybeSingle(),
      5000,
      'Perfil',
    )
    if (data?.role === 'agente') return '/workspace'
  } catch { /* fallback dashboard */ }
  return '/'
}

export function LoginPage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const session  = useAuthStore(s => s.session)
  const profile  = useAuthStore(s => s.profile)
  const nextParam = safeNextPath(params.get('next'))

  const [identifier, setIdentifier] = useState('')
  const [password, setPassword]     = useState('')
  const [showPass, setShowPass]     = useState(false)
  const [submitting, setSub]        = useState(false)
  const [error, setError]           = useState<string | null>(null)

  // Sesión ya activa (F5 en login)
  useEffect(() => {
    if (!session || submitting) return
    const role = profile?.role
    if (nextParam) {
      navigate(nextParam, { replace: true })
      return
    }
    if (role === 'agente') {
      navigate('/workspace', { replace: true })
      return
    }
    if (role) navigate('/', { replace: true })
  }, [session, profile, navigate, nextParam, submitting])

  const expired  = params.get('reason') === 'inactivity'
  const disabled = params.get('reason') === 'disabled'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!identifier || !password || submitting) return
    setSub(true)
    setError(null)

    try {
      const email = await resolveCredential(identifier.trim())

      const { data, error: authErr } = await withTimeout(
        supabase.auth.signInWithPassword({ email, password }),
        15000,
        'El servidor de autenticación no respondió',
      )

      if (authErr) {
        setError(
          authErr.message.toLowerCase().includes('invalid')
            ? 'Credenciales incorrectas. Revisa tu usuario y contraseña.'
            : authErr.message,
        )
        return
      }

      if (data.session) {
        useAuthStore.getState().setSession(data.session)
        useAuthStore.getState().setLoading(false)

        const dest = await resolvePostLoginPath(data.session.user.id, nextParam)
        navigate(dest, { replace: true })
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Error al iniciar sesión.')
    } finally {
      setSub(false)
    }
  }

  const isEmail = identifier.includes('@')

  return (
    <div className="min-h-screen w-full flex flex-col relative" style={{ background: 'linear-gradient(135deg, #050d1f 0%, #0a1628 40%, #0d1f3c 100%)' }}>
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: `radial-gradient(circle, #4f7dff 1px, transparent 1px)`,
          backgroundSize: '48px 48px',
        }} />
      </div>

      <div className="relative z-10 flex flex-col flex-1">
        <div className="flex items-center gap-4 px-10 pt-10 pb-4">
          <img src={LOGO_PROCREDITO} alt="ProCredito" className="h-16 w-16 object-contain drop-shadow-lg" />
          <div>
            <p className="text-2xl font-extrabold text-white tracking-tight leading-none">PROCREDITO</p>
            <p className="text-sm text-blue-200/70 tracking-[0.22em] uppercase mt-0.5">D O M I N I C A N A</p>
          </div>
          {isDesktopApp && (
            <div className="flex items-center gap-2 ml-6 pl-6 border-l border-white/[0.10]">
              <Headphones size={22} className="text-[#4f9ef8]" strokeWidth={1.8} />
              <div>
                <p className="text-lg font-extrabold text-white tracking-tight leading-none">CALL CENTER</p>
                <p className="text-[11px] text-[#4f9ef8] tracking-[0.18em] uppercase mt-0.5">PROCREDITO</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-1 flex-col lg:flex-row items-center lg:items-stretch px-8 lg:px-10 gap-10 lg:gap-16 pb-10">
          <div className="flex flex-col justify-center flex-1 max-w-xl lg:max-w-none lg:flex-1 pt-4 lg:pt-0">
            <p className="text-[#4f9ef8] text-sm font-semibold tracking-widest uppercase mb-4">
              {isDesktopApp ? 'CALL CENTER PROCREDITO' : 'PROCREDITO ENTERPRISE'}
            </p>
            <h1 className="text-3xl sm:text-4xl lg:text-[42px] font-bold text-white leading-tight mb-5">
              {isDesktopApp ? <>Más llamadas, más<br />oportunidades, más clientes.</> : <>Control inteligente<br />de operaciones y cartera</>}
            </h1>
            <p className="text-blue-200/60 text-base leading-relaxed mb-6 max-w-lg">
              {isDesktopApp ? 'Workspace del Call Center — recibe y realiza llamadas, gestiona tus campañas asignadas.' : 'Agentes: al entrar van al Workspace Call Center. Supervisores y admin al CRM.'}
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-2 xl:grid-cols-4 gap-3">
              {(isDesktopApp ? [
                { icon: Phone,       title: 'Llamadas a clientes',        sub: 'Recibe y marca sin salir de la app' },
                { icon: Users2,      title: 'Seguimiento en tiempo real', sub: 'Todo el equipo sincronizado' },
                { icon: Target,      title: 'Más cierres y resultados',   sub: 'Campañas y metas claras' },
                { icon: ShieldCheck, title: 'Trabajo en equipo y enfoque', sub: 'Supervisión y soporte constante' },
              ] : [
                { icon: ShieldCheck, title: 'Seguridad empresarial',      sub: 'Protección de datos avanzada' },
                { icon: BarChart2,   title: 'Información en tiempo real', sub: 'Datos actualizados al instante' },
                { icon: Settings2,   title: 'Gestión eficiente',          sub: 'Herramientas para mejores resultados' },
                { icon: Users2,      title: 'Acceso autorizado',          sub: 'Control de usuarios y permisos' },
              ]).map(({ icon: Icon, title, sub }) => (
                <div key={title} className="flex flex-col items-center text-center rounded-xl border border-white/[0.08] bg-white/[0.04] p-4 backdrop-blur-sm">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg mb-3" style={{ background: 'rgba(45,94,255,0.15)' }}>
                    <Icon size={18} strokeWidth={1.6} className="text-[#4f9ef8]" />
                  </div>
                  <p className="text-white text-xs font-semibold leading-snug mb-1">{title}</p>
                  <p className="text-blue-200/45 text-[10px] leading-snug">{sub}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="w-full lg:w-[420px] xl:w-[440px] shrink-0 flex items-center">
            <div className="w-full rounded-2xl border border-white/[0.10] p-8"
              style={{ background: 'rgba(8,16,32,0.75)', backdropFilter: 'blur(20px)' }}>

              <h2 className="text-2xl font-bold text-white mb-1">Iniciar sesión</h2>
              <p className="text-blue-200/50 text-sm mb-5">{isDesktopApp ? 'Accede a tu cuenta de Call Center' : 'Accede a tu cuenta para continuar'}</p>

              {expired && (
                <div className="flex items-start gap-2.5 rounded-xl border border-amber-500/25 bg-amber-500/10 px-4 py-3 mb-5">
                  <Clock size={14} className="text-amber-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-400">Sesión cerrada por inactividad.</p>
                </div>
              )}

              {disabled && (
                <div className="flex items-start gap-2.5 rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-3 mb-5">
                  <Ban size={14} className="text-red-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-red-400">Cuenta desactivada.</p>
                </div>
              )}

              {error && (
                <div className="flex items-start gap-2.5 rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-3 mb-5">
                  <AlertCircle size={14} className="text-red-400 shrink-0 mt-0.5" />
                  <p className="text-xs text-red-400 leading-relaxed">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-blue-100/80 mb-2">Usuario o correo electrónico</label>
                  <input
                    type="text"
                    value={identifier}
                    onChange={e => setIdentifier(e.target.value)}
                    placeholder="tu_usuario  ó  tu@email.com"
                    autoComplete="username"
                    required
                    disabled={submitting}
                    className={cn(
                      'w-full px-4 py-3 rounded-xl text-sm',
                      'border border-white/[0.10] bg-white/[0.05]',
                      'text-white placeholder:text-white/25',
                      'outline-none focus:border-[#2d5eff]/70',
                      'disabled:opacity-50',
                    )}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-blue-100/80 mb-2">Contraseña</label>
                  <div className="relative">
                    <input
                      type={showPass ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Ingresa tu contraseña"
                      autoComplete="current-password"
                      required
                      disabled={submitting}
                      className={cn(
                        'w-full pl-4 pr-12 py-3 rounded-xl text-sm',
                        'border border-white/[0.10] bg-white/[0.05]',
                        'text-white placeholder:text-white/25',
                        'outline-none focus:border-[#2d5eff]/70',
                        'disabled:opacity-50',
                      )}
                    />
                    <button type="button" tabIndex={-1} onClick={() => setShowPass(v => !v)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-white/25 hover:text-white/60">
                      {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting || !identifier || !password}
                  className={cn(
                    'w-full py-3.5 rounded-xl text-sm font-semibold text-white mt-2',
                    'disabled:opacity-40 disabled:cursor-not-allowed',
                  )}
                  style={{ background: 'linear-gradient(135deg, #1a4fff 0%, #2d6bff 100%)' }}
                >
                  <span className="flex items-center justify-center gap-2">
                    {submitting && <Loader2 size={14} className="animate-spin" />}
                    {submitting ? 'Verificando...' : 'Iniciar sesión'}
                  </span>
                </button>
              </form>
            </div>
          </div>
        </div>

        <div className="relative z-10 text-center py-4 border-t border-white/[0.05]">
          <p className="text-[11px] text-blue-200/25">© 2026 Procredito Dominicana</p>
        </div>
      </div>
    </div>
  )
}
